// Shared geometry + rendering for fortress patterns, used by the read-only
// catalogue (`FortressLibrary`) and the editor (`FortressEditor`).
//
// A pattern is a set of buildings at relative node-vector offsets. We anchor the
// offset frame on a real A-type node (`BASE`) so sublattice/parity line up for
// hex enumeration and path-finding.

import type { FortressPattern, FortressShape } from "@fortress/shared";

export type Cube = { q: number; r: number; s: number };

const SQRT3 = Math.sqrt(3);
/** Node-vector → pixel (the board's pointy-top axial layout). */
export const px = (c: Cube) => ({ x: SQRT3 * (c.q + c.r / 2), y: 1.5 * c.r });
export const addC = (a: Cube, b: Cube): Cube => ({
  q: a.q + b.q,
  r: a.r + b.r,
  s: a.s + b.s,
});
export const subC = (a: Cube, b: Cube): Cube => ({
  q: a.q - b.q,
  r: a.r - b.r,
  s: a.s - b.s,
});
export const keyC = (c: Cube) => `${c.q},${c.r},${c.s}`;
export const parseC = (k: string): Cube => {
  const [q, r, s] = k.split(",").map(Number);
  return { q: q!, r: r!, s: s! };
};
/** Reflection across the q-axis (swaps r and s) — the board's mirror symmetry. */
export const mirrorC = (c: Cube): Cube => ({ q: c.q, r: c.s, s: c.r });

// A node sits at `3*hexCentre + cornerOffset`; clockwise order, so these also
// trace a hexagon outline.
export const CORNER_OFFSETS: Cube[] = [
  { q: 2, r: -1, s: -1 },
  { q: 1, r: 1, s: -2 },
  { q: -1, r: 2, s: -1 },
  { q: -2, r: 1, s: 1 },
  { q: -1, r: -1, s: 2 },
  { q: 1, r: -2, s: 1 },
];
export const BASE: Cube = { q: 2, r: -1, s: -1 };

export type Dir = "N" | "NE" | "SE" | "S" | "SW" | "NW";
export const EDGE: Record<Dir, Cube> = {
  NE: { q: 2, r: -1, s: -1 },
  NW: { q: -1, r: -1, s: 2 },
  S: { q: -1, r: 2, s: -1 },
  N: { q: 1, r: -2, s: 1 },
  SE: { q: 1, r: 1, s: -2 },
  SW: { q: -2, r: 1, s: 1 },
};
const A_DIRS: Dir[] = ["NE", "NW", "S"];
const B_DIRS: Dir[] = ["N", "SE", "SW"];
const subOf = (nv: Cube): "A" | "B" => (((nv.q % 3) + 3) % 3 === 2 ? "A" : "B");
const dirsFor = (nv: Cube): Dir[] => (subOf(nv) === "A" ? A_DIRS : B_DIRS);

function neighborsOf(nv: Cube): { dir: Dir; nv: Cube }[] {
  return dirsFor(nv).map((d) => ({ dir: d, nv: addC(nv, EDGE[d]) }));
}

/** The (up to 3) hex centres a node-vector is a corner of. */
export function incidentHexes(n: Cube): Cube[] {
  const out: Cube[] = [];
  for (const co of CORNER_OFFSETS) {
    const d = { q: n.q - co.q, r: n.r - co.r, s: n.s - co.s };
    if (d.q % 3 === 0 && d.r % 3 === 0 && d.s % 3 === 0) {
      out.push({ q: d.q / 3, r: d.r / 3, s: d.s / 3 });
    }
  }
  return out;
}

/** A honeycomb patch flooded `depth` edges out from BASE (the editor grid). */
export function buildGrid(depth: number): Cube[] {
  const seen = new Map<string, Cube>([[keyC(BASE), BASE]]);
  let frontier = [BASE];
  for (let i = 0; i < depth; i++) {
    const next: Cube[] = [];
    for (const nv of frontier) {
      for (const { nv: nb } of neighborsOf(nv)) {
        if (!seen.has(keyC(nb))) {
          seen.set(keyC(nb), nb);
          next.push(nb);
        }
      }
    }
    frontier = next;
  }
  return [...seen.values()];
}

/** Shortest honeycomb path (edge directions) from an A-type origin to target. */
export function pathFrom(origin: Cube, target: Cube): Dir[] {
  if (keyC(origin) === keyC(target)) return [];
  const seen = new Set([keyC(origin)]);
  let frontier: { nv: Cube; path: Dir[] }[] = [{ nv: origin, path: [] }];
  for (let depth = 0; depth < 12 && frontier.length; depth++) {
    const next: { nv: Cube; path: Dir[] }[] = [];
    for (const { nv, path } of frontier) {
      for (const { dir, nv: nb } of neighborsOf(nv)) {
        const k = keyC(nb);
        if (seen.has(k)) continue;
        const np = [...path, dir];
        if (k === keyC(target)) return np;
        seen.add(k);
        next.push({ nv: nb, path: np });
      }
    }
    frontier = next;
  }
  return [];
}

/**
 * Support nodes that visually connect far-apart pieces of a pattern. Two
 * occupied nodes within 2 edge steps share a hex, so `PatternDiagram`'s
 * ≥2-corner rule already draws honeycomb between them; clusters farther apart
 * would render as floating glyphs with no visible connection (long
 * fortresses). Returns the interior nodes of shortest honeycomb paths linking
 * each cluster to the nearest already-linked one — counting these as occupied
 * completes the hex corridor across the gaps.
 */
function connectionNodes(occupied: ReadonlySet<string>): Set<string> {
  // Cluster the occupied nodes: same cluster when within 2 edge steps.
  const comp = new Map<string, number>();
  let comps = 0;
  for (const seed of occupied) {
    if (comp.has(seed)) continue;
    const stack = [seed];
    comp.set(seed, comps);
    while (stack.length) {
      const cur = parseC(stack.pop()!);
      for (const { nv: n1 } of neighborsOf(cur)) {
        for (const near of [n1, ...neighborsOf(n1).map((s) => s.nv)]) {
          const k = keyC(near);
          if (occupied.has(k) && !comp.has(k)) {
            comp.set(k, comps);
            stack.push(k);
          }
        }
      }
    }
    comps++;
  }
  const support = new Set<string>();
  if (comps <= 1) return support;

  // Grow a linked set outward from cluster 0: BFS the lattice from the whole
  // set to the nearest node of an unlinked cluster, absorb that cluster plus
  // the connecting path's nodes, repeat until every cluster is linked.
  const linked = new Set([...occupied].filter((k) => comp.get(k) === 0));
  for (let left = comps - 1; left > 0; left--) {
    const prev = new Map<string, string>();
    const seen = new Set(linked);
    let frontier = [...linked];
    let hit: string | undefined;
    while (!hit && frontier.length) {
      const next: string[] = [];
      for (const k of frontier) {
        for (const { nv } of neighborsOf(parseC(k))) {
          const nk = keyC(nv);
          if (seen.has(nk)) continue;
          seen.add(nk);
          prev.set(nk, k);
          if (occupied.has(nk) && !linked.has(nk)) {
            hit = nk;
            break;
          }
          next.push(nk);
        }
        if (hit) break;
      }
      frontier = next;
    }
    if (!hit) break; // unreachable: the lattice is connected
    const cluster = comp.get(hit)!;
    for (const k of occupied) if (comp.get(k) === cluster) linked.add(k);
    for (let k = prev.get(hit); k && !occupied.has(k); k = prev.get(k)) {
      support.add(k);
      linked.add(k);
    }
  }
  return support;
}

// --- Glyphs ---------------------------------------------------------------
//
// Card patterns draw every piece as a flat silhouette — scout ●, barracks ▲,
// mine ■, wild ★ (any building) — dark ink on a regular card, cream on a win
// card. Each sits in a halo of the panel colour (`--pattern-halo`), so lattice
// lines stop short of it and its node reads at a glance. X markers are the one
// coloured mark (terracotta), so they can't be mistaken for a piece or a line.

export const SHAPE_LABEL: Record<string, string> = {
  scout: "Scout",
  barracks: "Barracks",
  mine: "Mine",
  wild: "Wild",
};

/** Piece / marker / lattice colours per face. The halo and cell fill are CSS
 * custom properties so each host panel can match its own background. */
const PATTERN_INK = {
  light: {
    piece: "#1a1a22",
    marker: "#a84b32",
    halo: "var(--pattern-halo, #f3ecdb)",
    cell: "var(--pattern-cell, #efe5cd)",
    cellLine: "#d5c8a8",
    dot: "#ab9f82",
  },
  dark: {
    piece: "#faf6ea",
    marker: "#e2876d",
    halo: "var(--pattern-halo, #24242e)",
    cell: "var(--pattern-cell, rgba(250, 246, 234, 0.035))",
    cellLine: "rgba(250, 246, 234, 0.2)",
    dot: "rgba(250, 246, 234, 0.38)",
  },
} as const;

/** Each glyph's reach from its node, as a multiple of the base radius `r`
 * (the scout's radius). Used for drawing and for the diagram's fit box. */
const GLYPH_REACH: Record<string, number> = {
  scout: 1,
  mine: 1.22, // corner of the 0.86r half-side square
  barracks: 1.28,
  wild: 1.4,
  marker: 0.72,
};

/** One building/marker glyph centred at (cx, cy); `r` is the base radius. */
export function Glyph({
  kind,
  cx,
  cy,
  r = 7,
  dark = false,
}: {
  kind: string;
  cx: number;
  cy: number;
  r?: number;
  /** Dark-panel palette (win fortresses): cream pieces. */
  dark?: boolean;
}) {
  const ink = dark ? PATTERN_INK.dark : PATTERN_INK.light;
  const halo = {
    stroke: ink.halo,
    strokeWidth: Math.max(1.2, r * 0.39),
    strokeLinejoin: "round" as const,
    paintOrder: "stroke" as const,
  };
  switch (kind) {
    case "scout":
      return <circle cx={cx} cy={cy} r={r} fill={ink.piece} style={halo} />;
    case "mine": {
      const s = r * 0.86;
      return (
        <rect
          x={cx - s}
          y={cy - s}
          width={2 * s}
          height={2 * s}
          rx={r * 0.12}
          fill={ink.piece}
          style={halo}
        />
      );
    }
    case "barracks": {
      // Centred on the node by its centroid.
      const R = r * 1.28;
      const pts = [
        [cx, cy - R],
        [cx + R * 0.866, cy + R * 0.5],
        [cx - R * 0.866, cy + R * 0.5],
      ];
      return (
        <polygon
          points={pts.map(([x, y]) => `${x},${y}`).join(" ")}
          fill={ink.piece}
          style={halo}
        />
      );
    }
    case "wild": {
      const R = r * 1.4;
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const rr = i % 2 ? R * 0.47 : R;
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        pts.push(`${cx + rr * Math.cos(a)},${cy + rr * Math.sin(a)}`);
      }
      return <polygon points={pts.join(" ")} fill={ink.piece} style={halo} />;
    }
    case "marker": {
      const d = r * 0.64;
      const path = `M ${cx - d} ${cy - d} L ${cx + d} ${cy + d} M ${cx + d} ${cy - d} L ${cx - d} ${cy + d}`;
      return (
        <g fill="none" strokeLinecap="round">
          <path d={path} style={{ stroke: ink.halo }} strokeWidth={Math.max(2.4, r * 0.71)} />
          <path d={path} stroke={ink.marker} strokeWidth={Math.max(1.3, r * 0.34)} />
        </g>
      );
    }
    default:
      return null;
  }
}

// --- Pattern ⇄ node-map helpers -------------------------------------------

/** Mirror every offset in a pattern (its reflected build orientation). */
export function mirrorPattern(p: FortressPattern): FortressPattern {
  return {
    buildings: p.buildings.map((b) => ({ shape: b.shape, offset: mirrorC(b.offset) })),
    markers: p.markers.map(mirrorC),
    bridges: p.bridges.map(([a, b]) => [mirrorC(a), mirrorC(b)]),
  };
}

/** Rotate a pattern by `steps` × 60° clockwise. Rotates the ABSOLUTE node
 * vectors about the offset frame's origin hex centre — the node lattice is
 * 6-fold symmetric about a hex centre (but only 3-fold about a node, so
 * rotating the raw offsets around `BASE` would leave the lattice). Display
 * aid only: the board matcher already tries every orientation. */
export function rotatePattern(
  p: FortressPattern,
  steps: number,
): FortressPattern {
  const n = ((steps % 6) + 6) % 6;
  if (n === 0) return p;
  const rot60 = (c: Cube): Cube => ({ q: -c.r, r: -c.s, s: -c.q });
  const rotOffset = (o: Cube): Cube => {
    let nv = addC(BASE, o);
    for (let i = 0; i < n; i += 1) nv = rot60(nv);
    return subC(nv, BASE);
  };
  return {
    buildings: p.buildings.map((b) => ({ shape: b.shape, offset: rotOffset(b.offset) })),
    markers: p.markers.map(rotOffset),
    bridges: p.bridges.map(([a, b]) => [rotOffset(a), rotOffset(b)]),
  };
}

/** Editor state (node-key → shape | "marker") → a FortressPattern. The editor
 * can't author bridges (they live on edges, not nodes), so a card's original
 * bridges are passed through untouched. */
export function mapToPattern(
  shapes: Map<string, string>,
  bridges: FortressPattern["bridges"] = [],
): FortressPattern {
  const buildings: { shape: FortressShape; offset: Cube }[] = [];
  const markers: Cube[] = [];
  for (const [k, v] of shapes) {
    const offset = subC(parseC(k), BASE);
    if (v === "marker") markers.push(offset);
    else buildings.push({ shape: v as FortressShape, offset });
  }
  return { buildings, markers, bridges };
}

// --- Static rendering -----------------------------------------------------

/** A pattern's honeycomb crop in px-units (node spacing = √3·… — an edge is 3
 * units long): the hex cells, the corner dots, the pieces and bridges, and the
 * extent box including each glyph's reach. Shared by `PatternDiagram` and
 * `MirrorPair` (which fits a pattern and its mirror at ONE scale).
 *
 * Honeycomb crop is the same at every size: occupied nodes (buildings, markers,
 * bridge ends) plus empty corners that complete a hex shared by 2+ occupied
 * nodes. */
function patternLayout(pattern: FortressPattern, iconScale: number) {
  const patNodes = [
    ...pattern.buildings.map((b) => ({ nv: addC(BASE, b.offset), kind: b.shape as string })),
    ...pattern.markers.map((m) => ({ nv: addC(BASE, m), kind: "marker" })),
  ];
  const bridgeSegs = pattern.bridges.map(([a, b]) => ({
    a: addC(BASE, a),
    b: addC(BASE, b),
  }));

  // Every node that carries a building, marker, or bridge end.
  const occupied = new Set<string>();
  for (const pn of patNodes) occupied.add(keyC(pn.nv));
  for (const s of bridgeSegs) {
    occupied.add(keyC(s.a));
    occupied.add(keyC(s.b));
  }

  // Far-apart clusters get shortest-path support nodes so a hex corridor
  // connects them (otherwise long fortresses render as floating glyphs).
  const filled = new Set([...occupied, ...connectionNodes(occupied)]);

  // Candidate honeycomb cells: every hex that touches an occupied/support node.
  const candidateHexes = new Map<string, Cube>();
  for (const key of filled) {
    for (const h of incidentHexes(parseC(key))) candidateHexes.set(keyC(h), h);
  }

  const cornerDots = new Map<string, { x: number; y: number }>();
  const hexPolys: { x: number; y: number }[][] = [];
  // Always seed dots on occupied nodes so lonely pieces still have a node mark.
  for (const key of occupied) cornerDots.set(key, px(parseC(key)));
  for (const h of candidateHexes.values()) {
    const c = { q: 3 * h.q, r: 3 * h.r, s: 3 * h.s };
    const corners = CORNER_OFFSETS.map((co) => addC(c, co));
    // Only complete hexes that sit between 2+ occupied/support nodes — drops
    // the empty peripheral ring each lone marker would otherwise expand.
    if (corners.filter((nv) => filled.has(keyC(nv))).length >= 2) {
      hexPolys.push(
        corners.map((nv) => {
          const p = px(nv);
          cornerDots.set(keyC(nv), p);
          return p;
        }),
      );
    }
  }

  // Base glyph radius in px-units (an edge is 3 units): 0.28 of an edge at the
  // default iconScale (1.5), scaled with it for compact thumbnails.
  const baseR = 3 * 0.28 * (iconScale / 1.5);
  const exts = [
    ...[...cornerDots.values()].map((p) => ({ x: p.x, y: p.y, pad: 0 })),
    ...patNodes.map((pn) => ({
      ...px(pn.nv),
      pad: baseR * ((GLYPH_REACH[pn.kind] ?? 1) + 0.2),
    })),
  ];
  const minX = Math.min(...exts.map((e) => e.x - e.pad));
  const maxX = Math.max(...exts.map((e) => e.x + e.pad));
  const minY = Math.min(...exts.map((e) => e.y - e.pad));
  const maxY = Math.max(...exts.map((e) => e.y + e.pad));
  return {
    patNodes,
    bridgeSegs,
    hexPolys,
    dots: [...cornerDots.values()],
    minX,
    minY,
    w: maxX - minX || 1,
    h: maxY - minY || 1,
  };
}

type PatternLayout = ReturnType<typeof patternLayout>;

/** px per unit that fits `lay` inside maxW × maxH (minus `pad`), capped so a
 * tiny pattern doesn't balloon. */
function fitScale(lay: PatternLayout, maxW: number, maxH: number, pad: number) {
  return Math.min(15, (maxW - 2 * pad) / lay.w, (maxH - 2 * pad) / lay.h);
}

/** Draw a fortress pattern on its honeycomb: the hex cells the buildings sit on,
 * corner dots, then the pieces and X markers on their nodes and the bridges on
 * their edges. Callers vary `maxW` / `maxH` / `pad` / `iconScale`; `scale`
 * forces a px-per-unit (MirrorPair uses it to draw both orientations alike). */
export function PatternDiagram({
  pattern,
  maxW = 210,
  maxH = 150,
  pad = 14,
  iconScale = 1.5,
  dark = false,
  scale: forcedScale,
  label,
}: {
  pattern: FortressPattern;
  maxW?: number;
  maxH?: number;
  /** Margin (px) around the pattern inside the SVG. Shrink it for small
   * thumbnails where the default would crowd out the pattern itself. */
  pad?: number;
  /** Overall glyph-size multiplier. The default (1.5) suits the large expanded
   * card face; small market/fortress tiles pass a smaller value so their icons
   * stay compact. */
  iconScale?: number;
  /** Dark-panel palette for win fortresses (honeycomb + pieces retuned for a
   * near-black background). */
  dark?: boolean;
  /** Fixed px per unit (overrides the maxW/maxH fit). */
  scale?: number;
  /** Accessible description; omitted = decorative. */
  label?: string;
}) {
  const ink = dark ? PATTERN_INK.dark : PATTERN_INK.light;
  if (
    pattern.buildings.length === 0 &&
    pattern.markers.length === 0 &&
    pattern.bridges.length === 0
  ) {
    return <div style={{ fontSize: 11, color: dark ? "#8a8a94" : "#999" }}>no pattern</div>;
  }
  const lay = patternLayout(pattern, iconScale);
  const scale = forcedScale ?? fitScale(lay, maxW, maxH, pad);
  const sx = (x: number) => pad + (x - lay.minX) * scale;
  const sy = (y: number) => pad + (y - lay.minY) * scale;
  const cw = lay.w * scale + 2 * pad;
  const ch = lay.h * scale + 2 * pad;
  const E = 3 * scale; // edge length in px
  const r = Math.max(3 * iconScale, E * 0.28 * (iconScale / 1.5));
  const big = E >= 14; // room for a hollow bridge beam, cell fills, fat lines

  return (
    <svg
      width={cw}
      height={ch}
      viewBox={`0 0 ${cw} ${ch}`}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      {lay.hexPolys.map((pts, i) => (
        <polygon
          key={`h${i}`}
          points={pts.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ")}
          style={{ fill: big ? ink.cell : "none" }}
          stroke={ink.cellLine}
          strokeWidth={big ? 1.3 : 1}
          strokeLinejoin="round"
        />
      ))}
      {lay.dots.map((p, i) => (
        <circle
          key={`d${i}`}
          cx={sx(p.x)}
          cy={sy(p.y)}
          r={Math.max(1.2, E * 0.05)}
          fill={ink.dot}
        />
      ))}
      {/* X markers first, the pieces over them… */}
      {[...lay.patNodes]
        .sort((a, b) => Number(a.kind !== "marker") - Number(b.kind !== "marker"))
        .map((n, i) => {
          const p = px(n.nv);
          return <Glyph key={`g${i}`} kind={n.kind} cx={sx(p.x)} cy={sy(p.y)} r={r} dark={dark} />;
        })}
      {/* …and the bridges on top of everything: a hollow I-beam (the board's
          bridge glyph) laid along its edge, kept clear of the end pieces. */}
      {lay.bridgeSegs.map((s, i) => {
        const pa = px(s.a);
        const pb = px(s.b);
        const ax = sx(pa.x);
        const ay = sy(pa.y);
        const bx = sx(pb.x);
        const by = sy(pb.y);
        const L = Math.hypot(bx - ax, by - ay) || 1;
        const ux = (bx - ax) / L;
        const uy = (by - ay) / L;
        const ins = L * 0.31;
        const fl = L * 0.15;
        const p0 = { x: ax + ux * ins, y: ay + uy * ins };
        const p1 = { x: ax + ux * (L - ins), y: ay + uy * (L - ins) };
        const d =
          `M ${p0.x} ${p0.y} L ${p1.x} ${p1.y} ` +
          `M ${p0.x + uy * fl} ${p0.y - ux * fl} L ${p0.x - uy * fl} ${p0.y + ux * fl} ` +
          `M ${p1.x + uy * fl} ${p1.y - ux * fl} L ${p1.x - uy * fl} ${p1.y + ux * fl}`;
        return (
          <g key={`b${i}`} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d={d} stroke={ink.piece} strokeWidth={Math.max(1.4, L * 0.19)} />
            {big && <path d={d} style={{ stroke: ink.halo }} strokeWidth={L * 0.085} />}
          </g>
        );
      })}
    </svg>
  );
}

/** A pattern and its mirror, side by side at ONE scale with a dashed rule
 * between — matches the cards, where both build orientations are shown (the
 * matcher tries reflections). `maxW` / `maxH` bound EACH side. */
export function MirrorPair({
  pattern,
  dark = false,
  maxW = 132,
  maxH = 176,
  label,
}: {
  pattern: FortressPattern;
  dark?: boolean;
  maxW?: number;
  maxH?: number;
  label?: string;
}) {
  const mirrored = mirrorPattern(pattern);
  const pad = 6;
  const scale = Math.min(
    13,
    fitScale(patternLayout(pattern, 1.5), maxW, maxH, pad),
    fitScale(patternLayout(mirrored, 1.5), maxW, maxH, pad),
  );
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, minWidth: 0 }}>
      <div style={{ flex: "0 1 auto", minWidth: 0 }}>
        <PatternDiagram pattern={pattern} dark={dark} pad={pad} scale={scale} label={label} />
      </div>
      <span
        aria-hidden
        style={{
          alignSelf: "stretch",
          margin: "10px 2px",
          borderLeft: `1.5px dashed ${dark ? "rgba(250, 246, 234, 0.22)" : "#d3c7a8"}`,
        }}
      />
      <div style={{ flex: "0 1 auto", minWidth: 0 }}>
        <PatternDiagram
          pattern={mirrored}
          dark={dark}
          pad={pad}
          scale={scale}
          label={label ? `${label} (mirrored)` : undefined}
        />
      </div>
    </div>
  );
}

// --- Editable grid --------------------------------------------------------

/** Cycle order when a node is clicked. */
export const CYCLE: (string | undefined)[] = [
  undefined,
  "scout",
  "barracks",
  "mine",
  "wild",
  "marker",
];

/** An interactive honeycomb: click a node to cycle its shape. `shapes` maps a
 * node key → shape|"marker"; `onCycle` is called with the clicked node key.
 * `dark` renders the win-fortress palette (same as `PatternDiagram`'s). */
export function EditableGrid({
  grid,
  shapes,
  onCycle,
  dark = false,
}: {
  grid: Cube[];
  shapes: Map<string, string>;
  onCycle: (key: string) => void;
  dark?: boolean;
}) {
  const hexMap = new Map<string, Cube>();
  for (const nv of grid) for (const h of incidentHexes(nv)) hexMap.set(keyC(h), h);
  const cornerDots = new Map<string, { x: number; y: number }>();
  const hexPolys = [...hexMap.values()].map((h) => {
    const c = { q: 3 * h.q, r: 3 * h.r, s: 3 * h.s };
    return CORNER_OFFSETS.map((co) => {
      const nv = addC(c, co);
      const p = px(nv);
      cornerDots.set(keyC(nv), p);
      return p;
    });
  });

  const allP = [...cornerDots.values()];
  const minX = Math.min(...allP.map((p) => p.x));
  const maxX = Math.max(...allP.map((p) => p.x));
  const minY = Math.min(...allP.map((p) => p.y));
  const maxY = Math.max(...allP.map((p) => p.y));
  const pad = 16;
  const scale = 16;
  const sx = (x: number) => pad + (x - minX) * scale;
  const sy = (y: number) => pad + (y - minY) * scale;
  const cw = (maxX - minX) * scale + 2 * pad;
  const ch = (maxY - minY) * scale + 2 * pad;

  return (
    <svg
      width={cw}
      height={ch}
      viewBox={`0 0 ${cw} ${ch}`}
      style={{
        display: "block",
        background: dark ? "#121218" : "#fbf7ec",
        borderRadius: 8,
      }}
    >
      {hexPolys.map((pts, i) => (
        <polygon
          key={`h${i}`}
          points={pts.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ")}
          fill="none"
          stroke={dark ? "#7a7465" : "#d8cfb8"}
          strokeWidth={1.2}
          strokeLinejoin="round"
        />
      ))}
      {grid.map((nv, i) => {
        const k = keyC(nv);
        const p = px(nv);
        const v = shapes.get(k);
        return (
          <g key={`n${i}`} style={{ cursor: "pointer" }} onClick={() => onCycle(k)}>
            {v ? (
              <Glyph kind={v} cx={sx(p.x)} cy={sy(p.y)} r={8} dark={dark} />
            ) : (
              <circle
                cx={sx(p.x)}
                cy={sy(p.y)}
                r={2.6}
                fill={dark ? "#a39c8a" : "#b3aa92"}
              />
            )}
            <circle cx={sx(p.x)} cy={sy(p.y)} r={10} fill="transparent" />
          </g>
        );
      })}
    </svg>
  );
}

/** Serialise editor state to a `pat(...)` call to paste into config/fortress.ts.
 * Anchors at an A-type building (top-left) so the first building reads `b(...)`.
 * The editor can't author bridges, so a card's original `bridges` are passed in
 * and re-emitted as `br(...)` paths (they'd otherwise be silently dropped). */
export function patternToTs(
  shapes: Map<string, string>,
  bridges: FortressPattern["bridges"] = [],
): string {
  const entries = [...shapes.entries()].map(([k, v]) => ({ nv: parseC(k), v }));
  if (entries.length === 0 && bridges.length === 0) return "pat([])";
  const buildings = entries.filter((e) => e.v !== "marker");
  const markers = entries.filter((e) => e.v === "marker");

  const topLeft = <T extends { nv: Cube }>(list: T[]): T[] =>
    [...list].sort((a, b) => {
      const pa = px(a.nv);
      const pb = px(b.nv);
      return pa.y - pb.y || pa.x - pb.x;
    });

  const aBuildings = buildings.filter((e) => subOf(e.nv) === "A");
  const origin = aBuildings.length ? topLeft(aBuildings)[0]!.nv : BASE;

  const dirArgs = (nv: Cube) =>
    pathFrom(origin, nv)
      .map((d) => `"${d}"`)
      .join(", ");

  const bLines = topLeft(buildings).map((e) => {
    const args = dirArgs(e.nv);
    return `  b("${e.v}"${args ? `, ${args}` : ""})`;
  });
  const mLines = topLeft(markers).map((e) => `[${dirArgs(e.nv)}]`);

  // A bridge serialises as the path to its nearer endpoint plus the step that
  // crosses the bridged edge (br()'s last step).
  const brLines = bridges.map(([a, b]) => {
    let nvA = addC(BASE, a);
    let nvB = addC(BASE, b);
    if (pathFrom(origin, nvB).length < pathFrom(origin, nvA).length) {
      [nvA, nvB] = [nvB, nvA];
    }
    const cross = dirsFor(nvA).find((d) => keyC(addC(nvA, EDGE[d])) === keyC(nvB));
    if (!cross) return "br(/* endpoints not adjacent */)";
    const steps = [...pathFrom(origin, nvA).map((d) => `"${d}"`), `"${cross}"`];
    return `br(${steps.join(", ")})`;
  });

  const buildingsBlock = `[\n${bLines.join(",\n")}\n]`;
  const args = [buildingsBlock];
  if (markers.length || brLines.length) args.push(`[${mLines.join(", ")}]`);
  if (brLines.length) args.push(`[${brLines.join(", ")}]`);
  return `pat(${args.join(", ")})`;
}
